import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, Router } from '@angular/router';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class PermissionGuard implements CanActivate {
  constructor(private auth: AuthService, private router: Router) {}

  canActivate(route: ActivatedRouteSnapshot): boolean {
    const toArray = (value: unknown): string[] => {
      if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
      if (typeof value === 'string' && value.trim()) return [value];
      return [];
    };

    // Supports legacy and current route metadata keys.
    const requiredClaims = [
      ...toArray(route.data['permission']),
      ...toArray(route.data['permissions']),
      ...toArray(route.data['roles'])
    ]
      .map(v => v.toLowerCase().trim())
      .filter(Boolean);

    // No claim requirement means route is open to any authenticated user.
    if (requiredClaims.length === 0) {
      return true;
    }

    // Admin override.
    if (this.auth.isAdmin()) {
      return true;
    }

    // Allow access if user has at least one required claim/role.
    const userClaims = new Set(this.auth.getPermissions().map(v => v.toLowerCase().trim()));
    const userRole = this.auth.getRole()?.toLowerCase().trim();
    if (userRole) userClaims.add(userRole);

    const hasRequiredClaim = requiredClaims.some(claim => userClaims.has(claim));
    if (hasRequiredClaim) {
      return true;
    }

    if (route.routeConfig?.path === 'dashboard') {
      const preferredRoute = this.auth.getPreferredLandingRoute();
      if (preferredRoute && preferredRoute !== '/dashboard') {
        this.router.navigateByUrl(preferredRoute);
        return false;
      }
    }

    this.router.navigate(['/unauthorized']);
    return false;
  }
}
